/** @module Auth0BetaOptInWithUserContext */
/* eslint-disable import/extensions */
import Auth0BetaOptInFeatureBase from './auth0-beta-opt-in-feature-base.mjs';

/**
 * @abstract
 */
class Auth0BetaOptInWithUserContext extends Auth0BetaOptInFeatureBase {
  constructor(options) {
    super(options);
    console.log(`Auth0BetaOptInWithUserContext init for flag => "${JSON.stringify(options)}"`);

    // only ever holds truthful context data
    this._actualUserContextData = null;
    // Potentially holds an array of all known `_actualUserContextData` objects over time
    this._foundUserContextHistoryData = [];
  }

  _toUserContext(keyValue, customProps = {}) {
    if (!keyValue) {
      console.log('_toUserContext => key value missing, using anonymous context');
      return {
        kind: 'user',
        anonymous: true,
      };
    }

    const ffUserContext = {
      kind: 'user',
      key: keyValue,
      ...customProps,
    };
    console.log(`_toUserContext => ${JSON.stringify(ffUserContext)}`);
    return ffUserContext;
  }

  _addUserContextToPersistedState() {
    if (!this._actualUserContextData) {
      throw new Error('Cannot persist user context unless the user is logged in.');
    }

    // add this._actualUserContextData to the array of users
    let newPersistedState = this._persistedBetaOptInData;

    if (!newPersistedState?.userContextHistory) {
      newPersistedState = {
        userContextHistory: [
          this._actualUserContextData,
        ],
      };
    } else {
      // remove the old if needed
      newPersistedState.userContextHistory = newPersistedState.userContextHistory.filter(uc => uc.userId !== this._actualUserContextData.userId);
      newPersistedState.userContextHistory.push(this._actualUserContextData);
    }

    this._saveOptInOptionsState(newPersistedState);
    this._restoreStateFromDisk();
  }

  _removeUserContextFromPersistedState() {
    if (!this._actualUserContextData) {
      throw new Error('Cannot remove a user context unless the user is logged in.');
    }

    // remove this._actualUserContextData to the array of users
    const newPersistedState = this._persistedBetaOptInData;

    if (!newPersistedState?.userContextHistory) {
      return;
    }

    newPersistedState.userContextHistory = newPersistedState.userContextHistory
      .filter(uc => uc.key !== this._actualUserContextData.key);
    this._saveOptInOptionsState(newPersistedState);
    this._restoreStateFromDisk();
  }

  _buildUserContextList() {
    // returns an array of context objects. Must resolve the flag for all context objects (a history of users logged into this machine)
    // if we are actually logged in, no guessing, just return the single, truthful context
    if (this._actualUserContextData) {
      return [this._toUserContext(this._actualUserContextData.sellerId, this._actualUserContextData)];
    }
    // otherwise, check stored data in _foundUserContextData
    if (!this._foundUserContextHistoryData || !this._foundUserContextHistoryData.length) {
      return [this._toUserContext()]; // anonymous context
    }
    return this._foundUserContextHistoryData.map(fcd => this._toUserContext(fcd.sellerId, fcd));
  }

  _restoreStateFromDisk() {
    const currentPersistedState = this._persistedBetaOptInData;
    // currentPersistedState?.userContextHistory is an array of any and all users for which user context has been recorded (i.e. multiple users on the same machine)
    this._foundUserContextHistoryData = currentPersistedState?.userContextHistory ?? [];
  }

  optIn() {
    throw new Error('Auth0BetaOptInWithUserContext is an abstract class, use a derived class');
  }

  optOut() {
    throw new Error('Auth0BetaOptInWithUserContext is an abstract class, use a derived class');
  }

  isOptedIn = async () => {
    throw new Error('Auth0BetaOptInWithUserContext is an abstract class, use a derived class');
  };
}

export default Auth0BetaOptInWithUserContext;
